import * as Actions from '../actions/invoiceBatchActions';
import InvoiceBatch from '../../entities/invoiceBatch';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type InvoiceBatchAction = { 
    type: string; 
    invoiceBatch: InvoiceBatch | null; 
    invoiceBatchId: string | InvoiceBatch; 
    partial: boolean; 
    lastFetch: number; 
    invoiceBatches: InvoiceBatch[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<InvoiceBatch> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {InvoiceBatch[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<InvoiceBatch> | null, firmId: string, userId: string, updates?: InvoiceBatch[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'invoiceBatch', 'invoiceBatches', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<InvoiceBatch>} data
 * @param {InvoiceBatch[]} [updates]
 * @returns {ArrayMap<InvoiceBatch>}
 */
function updateStorage(data: ArrayMap<InvoiceBatch>, updates?: InvoiceBatch[]): ArrayMap<InvoiceBatch> {
	if (logging) console.debug('⮂ invoiceBatchReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {InvoiceBatch[]} [updates]
 * @returns {ArrayMap<InvoiceBatch>}
 */
function updateStorageNoReturn(updates?: InvoiceBatch[]): void {
	if (logging) console.debug('⮂ invoiceBatchReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {InvoiceBatch[]} [data]
 * @returns {ArrayMap<InvoiceBatch>}
 */
function createState(data?: InvoiceBatch[] | ArrayMap<InvoiceBatch>): ArrayMap<InvoiceBatch> {
	if (!data) return ArrayMapRegistry.clone<InvoiceBatch>('invoiceBatch', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<InvoiceBatch>('invoiceBatch', data.map(e => e));
    else return ArrayMapRegistry.clone<InvoiceBatch>('invoiceBatch', data);
}

function invoiceBatches(state: ArrayMap<InvoiceBatch>, action: InvoiceBatchAction): ArrayMap<InvoiceBatch> {
	if (!state) state = createState();

	let st: ArrayMap<InvoiceBatch>;

	switch (action.type) {
		// action.invoiceBatch - the new value of invoiceBatch with id action.id
		// action.invoiceBatchId - id of the target, used if new value is null
		case Actions.INVOICEBATCH_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ invoiceBatchReducer: FETCH_ONE_SUCCESS', action);

			if (action.invoiceBatch === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.invoiceBatchId)) {
				st = state.clone();
				st.set(action.invoiceBatch);
				// state.clear();

				updateStorageNoReturn([action.invoiceBatch]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.invoiceBatches
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.invoiceBatches
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.invoiceBatches
			 !partial, !stateEmpty, fetchEmpty	-> D return invoiceBatches in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge invoiceBatches in state newer than those in action.invoiceBatches
		 */
		case Actions.INVOICEBATCH_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.invoiceBatches.length === 0;
			const fetched = action.invoiceBatches.map(invoiceBatch => new InvoiceBatch({...invoiceBatch, persisted: true}));

			if (logging) console.debug(`⮂ invoiceBatchReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} invoiceBatches with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ invoiceBatchReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ invoiceBatchReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<InvoiceBatch> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: InvoiceBatch[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(invoiceBatch => {
					const current = st.get(invoiceBatch.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && invoiceBatch.status === 'D') {
							// Remove it from state
							toUpdate.push(invoiceBatch);
							st.delete(invoiceBatch);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (invoiceBatch.addedOn > current.addedOn) {
								toUpdate.push(invoiceBatch);
								st.set(invoiceBatch);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (invoiceBatch.status !== 'D') {
							toUpdate.push(invoiceBatch);
							st.set(invoiceBatch);
						}
						else if (logging) console.debug('⮂ invoiceBatchReducer: partialMerge: skipping deleted invoiceBatch not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ invoiceBatchReducer: empty: invoiceBatches', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ invoiceBatchReducer: fullMerge: invoiceBatches', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localInvoiceBatch => {
                    // If this invoiceBatch has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localInvoiceBatch.persisted) {
                        const fetchedInvoiceBatch = st.get(localInvoiceBatch.id);
                        if (!fetchedInvoiceBatch || localInvoiceBatch.addedOn > fetchedInvoiceBatch.addedOn) st.set(localInvoiceBatch);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.INVOICEBATCH_FETCH_ONE_FAILURE:
			console.error('INVOICEBATCH_FETCH_ONE_FAILURE', action.invoiceBatchId, action.err);

			// No update to storage required
			return state;

		case Actions.INVOICEBATCH_FETCH_FAILURE:
			console.error('INVOICEBATCH_FETCH_FAILURE', action.invoiceBatchId, action.err);

			// No update to storage required
			return state;

		case Actions.INVOICEBATCH_RESTORE: {
		    let invoiceBatch = new InvoiceBatch({...action.invoiceBatch});
            st = state.clone();

            const existing = st.get(invoiceBatch.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (invoiceBatch.addedOn >= existing.addedOn) {
                    st.set(invoiceBatch);
                    return updateStorage(st, [invoiceBatch]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (invoiceBatch.status === 'D') return state;

                st.set(invoiceBatch);
                return updateStorage(st, [invoiceBatch]);
            }
        }
            
		case Actions.INVOICEBATCH_SAVE: {
		    // console.debug('⮂ INVOICEBATCH_SAVE reducer', action.invoiceBatch?.id);
		    
			// clone the invoiceBatch
			let invoiceBatch = new InvoiceBatch({...action.invoiceBatch});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new invoiceBatch in the state
			// If this is a create, add to the queue
			const _invoiceBatch = st.get(invoiceBatch.id);

			if (!_invoiceBatch) st.set(invoiceBatch);
			else {
				if (invoiceBatch.addedOn >= _invoiceBatch.addedOn) st.set(invoiceBatch);
			}

			return updateStorage(st, [invoiceBatch]);
		}

		case Actions.INVOICEBATCH_SAVE_MANY: {
		    if (logging) console.debug('⮂ INVOICEBATCH_SAVE_MANY reducer', action.invoiceBatches.map(invoiceBatch => invoiceBatch.id).join(','));
		    
			// clone the invoiceBatches
			let toProcess = action.invoiceBatches.map(invoiceBatch => new InvoiceBatch(invoiceBatch));

			st = state.clone();
			// state.clear();

			const updates: InvoiceBatch[] = [];

			toProcess.forEach(invoiceBatch => {
				// Add or update the new invoiceBatch in the state
				// If this is a create, add to the queue
				const existing = st.get(invoiceBatch.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (invoiceBatch.status !== 'D') {
						st.push(invoiceBatch);
						updates.push(invoiceBatch);
					}
					else if (logging) console.debug('⮂ INVOICEBATCH_SAVE_MANY: skipping deleted invoiceBatch not in state');
				}
				else {
					if (invoiceBatch.addedOn >= existing.addedOn) {
						st.set(invoiceBatch);
						updates.push(invoiceBatch);
					}
					else if (logging) console.debug('⮂ Skipping, existing invoiceBatch had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.INVOICEBATCH_REMOVE: {
			// clone the invoiceBatch
			const invoiceBatch = new InvoiceBatch({...action.invoiceBatch});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(invoiceBatch);

			return updateStorage(st, [invoiceBatch]);
		}

		case Actions.INVOICEBATCH_RESTORE_FAILURE:
		case Actions.INVOICEBATCH_SAVE_FAILURE: {
		    console.warn('INVOICEBATCH_SAVE_FAILURE reducer', action.invoiceBatch?.id);
		    if (!action.invoiceBatch?.id) {
				console.warn('INVOICEBATCH_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const invoiceBatch = ensure(InvoiceBatch, action.invoiceBatch);
			if (!invoiceBatch) {
				console.error('Could not find invoiceBatch after call to save');
				return state;
			}

			st = state.clone();

			const _invoiceBatchRef = state.get(invoiceBatch.id);

			if (_invoiceBatchRef && (_invoiceBatchRef.addedOn === invoiceBatch.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _invoiceBatch = new InvoiceBatch({..._invoiceBatchRef});
				_invoiceBatch.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_invoiceBatch.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('invoiceBatch', _invoiceBatch).then(r => {
						if (r.isErr()) console.error(`⮂ invoiceBatchReducer: failed to save invoiceBatch with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ invoiceBatchReducer: saved invoiceBatch with fatalError info');
					}).catch(err => console.error(`⮂ invoiceBatchReducer: failed to save invoiceBatch with fatalError info: ${err.message}`));

				st.set(_invoiceBatch);
			}

			return updateStorage(st);
		}

		case Actions.INVOICEBATCH_SAVE_MANY_FAILURE: {
		    console.warn('INVOICEBATCH_SAVE_MANY_FAILURE reducer', action.invoiceBatches.map(invoiceBatch => invoiceBatch.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.INVOICEBATCH_RESTORE_SUCCESS:
		case Actions.INVOICEBATCH_SAVE_SUCCESS: {
			if (!action.invoiceBatch?.id) {
				console.warn('INVOICEBATCH_SAVE_SUCCESS reducer');
                return state;
			}

			const invoiceBatchRef = state.get(action.invoiceBatch?.id);
			if (!invoiceBatchRef) {
				console.warn('Could not find invoiceBatch after call to save');
                return state;
			}

			st = state.clone();

			if (action.invoiceBatch.addedOn >= invoiceBatchRef.addedOn) {
				st.set(action.invoiceBatch);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const invoiceBatch = new InvoiceBatch({...invoiceBatchRef, persisted: true});
				st.set(invoiceBatch);
			}

			return updateStorage(st, [action.invoiceBatch]);
		}

		case Actions.INVOICEBATCH_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ INVOICEBATCH_SAVE_MANY_SUCCESS reducer', action.invoiceBatches.map(invoiceBatch => invoiceBatch.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const invoiceBatches: InvoiceBatch[] = [];

			action.invoiceBatches.forEach(e => {
				const invoiceBatchRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!invoiceBatchRef) console.warn('Could not find invoiceBatch after call to save');
				else if (e.addedOn >= invoiceBatchRef.addedOn) {
				    st.set(e);
				    invoiceBatches.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const invoiceBatch = new InvoiceBatch({...invoiceBatchRef, persisted: true});
					st.set(invoiceBatch);
					invoiceBatches.push(invoiceBatch);
				}
			});

			return updateStorage(st, invoiceBatches);
		}

		case Actions.INVOICEBATCH_REMOVE_FAILURE: {
		    console.warn('INVOICEBATCH_REMOVE_FAILURE action', action.invoiceBatch?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.INVOICEBATCH_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.INVOICEBATCH_RESTORE_MANY: {
			if (logging) console.debug('⮂ INVOICEBATCH_RESTORE_MANY reducer', action.invoiceBatches.map(invoiceBatch => invoiceBatch.id).join(','));

			const toProcess = action.invoiceBatches.map(invoiceBatch => new InvoiceBatch({...invoiceBatch}));
			st = state.clone();

			const updates: InvoiceBatch[] = [];

			toProcess.forEach(invoiceBatch => {
				const existing = st.get(invoiceBatch.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (invoiceBatch.addedOn >= existing.addedOn) {
						st.set(invoiceBatch);
						updates.push(invoiceBatch);
					}
					else if (logging) console.debug('⮂ INVOICEBATCH_RESTORE_MANY: skipping older version of invoiceBatch');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (invoiceBatch.status !== 'D') {
						st.set(invoiceBatch);
						updates.push(invoiceBatch);
					}
					else if (logging) console.debug('⮂ INVOICEBATCH_RESTORE_MANY: skipping deleted invoiceBatch not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.INVOICEBATCH_REMOVE_MANY: {
			if (logging) console.debug('⮂ INVOICEBATCH_REMOVE_MANY reducer', action.invoiceBatches.map(invoiceBatch => invoiceBatch.id).join(','));

			const toProcess = action.invoiceBatches.map(invoiceBatch => new InvoiceBatch({...invoiceBatch}));
			st = state.clone();

			toProcess.forEach(invoiceBatch => {
				st.delete(invoiceBatch);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default invoiceBatches;